import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Formik, FormikErrors } from 'formik';
import { LoginResultType, useLoginUserMutation } from 'src/redux/services/auth';
import Row from 'src/frontend-common/components/Row';
import { useNavigate } from 'react-router-dom';
import PasswordFormField from 'src/pages/Login/components/PasswordFormField';
import {
  CustomAlert,
  CustomButton,
  CustomDivider,
  CustomTextField,
  ForgotPasswordButton,
  GoogleButton,
  Heading,
  LabelOr,
  Line,
  LoginButton,
  Subtitle,
  Title,
} from 'src/pages/Login/forms/styledFormComponents';
import Google from 'src/frontend-common/components/Icons/Google';
import { config } from 'src/config';
import * as Yup from 'yup';

interface PropTypes {
  onLoginResponse: (result: LoginResultType, payload: Record<string, string>) => Promise<void>;
}

interface FormValues {
  username: string;
  password: string;
}

const LoginForm: React.FC<PropTypes> = ({ onLoginResponse }) => {
  let preSignUpError = '';
  const anchorLink = location.href.split('#')[1] ?? '';
  if (anchorLink !== '') {
    preSignUpError = decodeURIComponent(anchorLink.replace(/\+/g, ' ')).split('***')[1] ?? '';
  }
  const googleProviderUrl = `${config.API_PROTOCOL}://${config.API_HOST}/v1/auth/provider/google`;
  const [loginUser, { isLoading, isError }] = useLoginUserMutation();
  const navigate = useNavigate();
  const [socialIsLoading, setSocialIsLoading] = React.useState(false);
  const DisplayingErrorMessagesSchema = Yup.object().shape({
    username: Yup.string().required('This field is required'),
    password: Yup.string().required('This field is required'),
  });

  return (
    <>
      <Heading style={{ display: 'block' }}>
        <Title
          style={{
            textAlign: 'initial',
            fontSize: 40,
            fontWeight: 600,
            marginBottom: '-1%',
          }}
        >
          Login
        </Title>
        <Subtitle variant="body2" style={{ textAlign: 'initial' }}>
          Don&apos;t have an account?
          <LoginButton variant="text" style={{ color: 'black', marginLeft: -2 }} onClick={() => navigate('/auth/sign-up')}>
            Sign up
          </LoginButton>
        </Subtitle>
      </Heading>
      <Formik
        initialValues={{
          username: document.getElementById('username')?.innerHTML || '',
          password: document.getElementById('outlined-adornment-password')?.innerHTML || '',
          __all: '',
        }}
        validateOnChange={true}
        validationSchema={DisplayingErrorMessagesSchema}
        onSubmit={async (values, { setErrors }) => {
          const device_key = localStorage.getItem(`${values.username}_device_key`);
          const device_group_key = localStorage.getItem(`${values.username}_device_group_key`);
          const device_password = localStorage.getItem(`${values.username}_device_password`);
          let result;
          if (device_key && device_group_key && device_password) {
            result = await loginUser({
              username: values.username,
              password: values.password,
              device_group_key: device_group_key,
              device_key: device_key,
              device_password: device_password,
            });
          } else {
            const sessionId = uuidv4();

            if (process.env.REACT_APP_ENV !== 'development' && process.env.REACT_APP_ENV !== 'test') {
              await window.tf.sendRecord(sessionId, values.username);
            }

            result = await loginUser({
              username: values.username,
              password: values.password,
              session_id: sessionId,
            });
          }

          if ('error' in result && 'data' in result.error) {
            const violations = // @ts-ignore
              result.error?.data?.violations as FormikErrors<FormValues>;
            setErrors({ ...violations });
            return;
          }

          if ('data' in result) {
            onLoginResponse(result.data, values);
          }
        }}
      >
        {(formikProps) => {
          const {
            errors: { username: usernameError, password: passwordError, __all: genericErrors },
            touched: { username: usernameTouched, password: passwordTouched },
          } = formikProps;

          return (
            <>
              {((isError && genericErrors) || preSignUpError) && <CustomAlert severity="error">{genericErrors ?? preSignUpError}</CustomAlert>}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  formikProps.handleSubmit();
                }}
                onKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    formikProps.submitForm();
                  }
                }}
              >
                <CustomTextField
                  id="username"
                  variant="outlined"
                  label="Email address"
                  error={usernameTouched && !!usernameError}
                  helperText={usernameTouched && usernameError}
                  inputProps={{ 'fido-ignore': 'true', 'data-tf': 'username' }}
                  autoComplete="off"
                  {...formikProps.getFieldProps('username')}
                />

                <PasswordFormField passwordError={passwordError} passwordTouched={passwordTouched} fieldProps={formikProps.getFieldProps('password')} />

                <Row hAlign="flex-start">
                  <ForgotPasswordButton variant="text" style={{ color: 'black' }} onClick={() => navigate('/auth/forgot-password')}>
                    Forgot password?
                  </ForgotPasswordButton>
                </Row>
              </form>

              <CustomButton
                variant="contained"
                style={{ backgroundColor: 'black', color: 'white' }}
                disabled={isLoading}
                onClick={() => formikProps.submitForm()}
              >
                {isLoading ? 'Loading...' : 'Login'}
              </CustomButton>

              <CustomDivider hAlign="center" centered>
                <LabelOr>Or</LabelOr>
                <Line variant="fullWidth" />
              </CustomDivider>

              <GoogleButton
                variant="contained"
                color="primary"
                disableElevation
                disabled={socialIsLoading}
                onClick={() => {
                  setSocialIsLoading(true);
                  navigate('/loading', { state: { url: googleProviderUrl } });
                }}
              >
                <Google fontSize={16} style={{ marginRight: 8 }} />
                {isLoading || socialIsLoading ? 'Loading...' : 'Continue with Google'}
              </GoogleButton>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default LoginForm;
